import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createRateRequestAPI,
  getOnboardingAPI,
} from "services/user-controllers/onboardingController";
import ChargesTable from "./ChargesTable";
import { flattenCharges, stateChargeItems } from "./chargeUtils";
import RateMatrix from "./RateMatrix";
import PincodeCheck from "./PincodeCheck";
import StateCharges from "./StateCharges";
import ZoneCoverage, { ZonePincodesModal } from "./ZoneCoverage";
import { MdAccessTime, MdOutlineVerifiedUser } from "react-icons/md";
import RequestChangesList from "./RequestChangesList";
import "./Onboarding.scss";

const EMPTY_DRAFT = { rates: {}, stdTat: {}, odaTat: {}, charges: {} };

const show = (value, unit = "") => (unit.startsWith("₹") ? `₹${value}${unit.slice(1)}` : `${value}${unit ? ` ${unit}` : ""}`);

// "Commercials & TAT" - the customer's rates, TAT and charges. In edit mode the customer can
// propose their own values; MV Load's current values stay as they are until a request is approved.
const StandardRates = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [reviewing, setReviewing] = useState(false);
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [zoneView, setZoneView] = useState(null);

  const load = async () => {
    try {
      const { data: res } = await getOnboardingAPI();
      if (res?.code === 200) setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load your rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const card = data?.rateCard;
  const requests = data?.rateRequests || [];
  const pendingRequest = requests.find((r) => r.status === "pending");
  const hasPending = !!pendingRequest;

  // draft entries that really differ from the current value
  const changes = useMemo(() => {
    if (!card) return [];
    const zoneName = {};
    card.zones.forEach((z) => {
      zoneName[z.id] = z.name;
    });
    const out = [];
    [
      ["rates", "Rate (₹/kg)", "₹/kg"],
      ["stdTat", "Standard TAT (days)", "days"],
      ["odaTat", "ODA TAT (days)", "days"],
    ].forEach(([kind, group, unit]) => {
      Object.entries(draft[kind]).forEach(([key, raw]) => {
        const [from, to] = key.split("-");
        const proposed = parseFloat(raw);
        const current = card[kind][key];
        if (current === undefined || !(proposed > 0) || proposed === Number(current)) return;
        out.push({ kind, key, group, unit, label: `${zoneName[from]} → ${zoneName[to]}`, current: Number(current), proposed });
      });
    });
    [...flattenCharges(card.charges), ...stateChargeItems(data?.stateCharges)].forEach((c) => {
      const raw = draft.charges[c.key];
      const proposed = parseFloat(raw);
      if (raw === undefined || raw === "" || !(proposed >= 0) || proposed === c.value) return;
      out.push({ kind: "charges", key: c.key, group: c.group, unit: c.unit, label: c.name, current: c.value, proposed });
    });
    return out;
  }, [draft, card, data]);

  const setCell = (kind) => (key, value) =>
    setDraft((prev) => ({ ...prev, [kind]: { ...prev[kind], [key]: value } }));

  // editing while a request is pending starts from that request's values; sending replaces it
  const startEditing = () => {
    if (pendingRequest) {
      const next = { rates: {}, stdTat: {}, odaTat: {}, charges: {} };
      pendingRequest.changes.forEach((c) => {
        next[c.kind][c.key] = c.proposed;
      });
      setDraft(next);
      setReason(pendingRequest.reason || "");
    }
    setEditing(true);
  };

  const stopEditing = () => {
    setEditing(false);
    setDraft(EMPTY_DRAFT);
  };

  const sendRequest = async () => {
    if (reason.trim().length < 5) return toast.error("Please add a reason (at least 5 characters)");
    const proposed = { rates: {}, stdTat: {}, odaTat: {}, charges: {} };
    changes.forEach((c) => {
      proposed[c.kind][c.key] = c.proposed;
    });
    try {
      setSending(true);
      const { data: res } = await createRateRequestAPI({ proposed, reason: reason.trim() });
      if (res?.code === 200) {
        setData(res.data);
        toast.success("Your request has been sent to MV Load");
        setReviewing(false);
        setReason("");
        stopEditing();
      } else {
        toast.error(res?.message || "Failed to send the request");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to send the request");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="ob_page">Loading…</div>;

  const hasRates = data?.isLead && card?.zones?.length > 0;
  // only when the vendor really set ODA TAT values (blank / 0 means not set)
  const hasOda = Object.values(card?.odaTat || {}).some((v) => v !== "" && v != null && Number(v) > 0);

  return (
    <div className="ob_page">
      {hasRates && (
        <div className={`ob_lock_banner ${card.locked ? "ok" : "warn"}`}>
          <span className="icon">
            <MdOutlineVerifiedUser />
          </span>
          <div className="text">
            <strong>
              {card.locked ? "Charges Accepted & Locked — Your Contract is Being Prepared" : "Rates Not Locked Yet"}
            </strong>
            <p>
              {card.locked
                ? `${data?.contract?.acceptedAt ? "You accepted & locked these rates" : "MV Load has locked these rates"}${
                    data?.contract?.acceptedAt
                      ? ` on ${new Date(data.contract.acceptedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                      : ""
                  }. All rates below are now frozen in your contract and can no longer be edited.`
                : "Review the rates below. MV Load will set and lock your final rates into your contract."}
            </p>
          </div>
          <span className="pill">{card.locked ? "Rates Locked ✓" : "Pending"}</span>
        </div>
      )}

      <div className="ob_title">
        <div>
          <h3>
            Commercials &amp; TAT
            {hasRates && <span className="ob_pill warn">Contract Standard</span>}
          </h3>
          <p>
            Review standard contracted shipping rates, guaranteed delivery TAT across zones, and request custom rate
            amendments.
          </p>
        </div>
        {hasRates && !card.locked && (
          <div className="ob_title_actions">
            {!editing ? (
              <button type="button" className="ob_btn outline" onClick={startEditing}>
                Edit Rates
              </button>
            ) : (
              <>
                <button type="button" className="ob_btn outline" onClick={stopEditing}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="ob_btn primary"
                  disabled={!changes.length}
                  onClick={() => setReviewing(true)}
                >
                  Review &amp; Send{changes.length ? ` (${changes.length})` : ""}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <PincodeCheck />

      {!hasRates && (
        <div className="ob_card ob_center">
          <h4>No rates available</h4>
          <p>No standard rate card has been set up for your account yet.</p>
        </div>
      )}

      {hasRates && (
        <>
          {hasPending && (
            <div className="ob_banner warn">
              Your change request is with MV Load. You can still edit and send again — the new request replaces this one. MV Load will set the final rates and lock them.
            </div>
          )}

          <ZoneCoverage zones={card.zones} onSelect={setZoneView} />
          <RateMatrix
            title={`${card.zones.length}x${card.zones.length} Standard Zone Rates Matrix (₹ / kg)`}
            subtitle="Origin Zone (Rows) × Destination Zone (Columns) · Click zone headers to inspect covered pincodes"
            zones={card.zones}
            values={card.rates}
            editable={editing}
            draft={draft.rates}
            onChange={setCell("rates")}
            onZoneClick={setZoneView}
          />
          <RateMatrix
            icon={MdAccessTime}
            title="Standard TAT (days)"
            subtitle="Origin zone (rows) × Destination zone (columns)"
            zones={card.zones}
            values={card.stdTat}
            editable={editing}
            draft={draft.stdTat}
            onChange={setCell("stdTat")}
          />
          {hasOda && (
            <RateMatrix
              icon={MdAccessTime}
              title="ODA TAT (days)"
              subtitle="Applies when the origin or destination is an ODA pincode"
              zones={card.zones}
              values={card.odaTat}
              editable={editing}
              draft={draft.odaTat}
              onChange={setCell("odaTat")}
            />
          )}
          <ChargesTable
            charges={card.charges || []}
            editable={editing}
            draft={draft.charges}
            onChange={setCell("charges")}
          />
          <StateCharges
            stateCharges={data?.stateCharges}
            editable={editing}
            draft={draft.charges}
            onChange={setCell("charges")}
          />
          <RequestChangesList requests={requests} />
        </>
      )}

      {zoneView && <ZonePincodesModal zone={zoneView} onClose={() => setZoneView(null)} />}

      {reviewing && (
        <div className="ob_modal_overlay" onClick={() => !sending && setReviewing(false)}>
          <div className="ob_modal" onClick={(e) => e.stopPropagation()}>
            <h4>Review your request</h4>
            <p className="ob_note">
              {changes.length} change{changes.length > 1 ? "s" : ""} will be sent to MV Load. Both rates are kept on the
              request.{hasPending ? " This replaces your earlier request that is still waiting." : ""}
            </p>
            <div className="ob_modal_table">
              <table className="ob_charges changes">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>MV Load rate</th>
                    <th>Your rate</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c) => {
                    const pct = c.current ? Math.round(((c.proposed - c.current) / c.current) * 100) : 0;
                    const veryLow = (c.kind === "rates" || c.kind === "charges") && pct <= -20;
                    return (
                      <tr key={c.kind + c.key}>
                        <td>
                          {c.group} · {c.label}
                        </td>
                        <td>{show(c.current, c.unit)}</td>
                        <td>
                          <strong>{show(c.proposed, c.unit)}</strong>
                        </td>
                        <td className={veryLow ? "bad" : ""}>
                          {pct > 0 ? "+" : ""}
                          {pct}%{veryLow ? " · Very Low" : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <label className="ob_label">Reason for the change *</label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. We ship 5 tons a month and expect a volume-based rate"
            />
            <div className="ob_modal_actions">
              <button type="button" className="ob_btn outline" disabled={sending} onClick={() => setReviewing(false)}>
                Back
              </button>
              <button type="button" className="ob_btn primary" disabled={sending} onClick={sendRequest}>
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardRates;
